/**
 * The setup wizard's half of the conflict-aware save: send only the keys the
 * admin actually changed since the wizard was seeded from the org row.
 *
 * Why: the wizard passes no expected version (it has no conflict UI), and a
 * key it did not touch carries the value it was SEEDED with — which may be
 * older than what the profile editor flushed at wizard-open. Re-sending that
 * seeded copy would silently overwrite the newer value. Sending only the
 * changed keys means an untouched field is never written at all.
 *
 * Pure. Structural comparison, key-order insensitive — see
 * `src/lib/structuralEquality.ts`, shared with the profile editor.
 */
import { same } from "@/lib/structuralEquality";
export { same };

/**
 * The subset of `keys` whose value in `current` differs from `seed`, in the
 * order given. A key absent from both is never reported.
 */
export const changedKeys = <T extends Record<string, unknown>>(
  seed: T,
  current: T,
  keys: readonly (keyof T & string)[],
): (keyof T & string)[] => keys.filter((key) => !same(seed[key], current[key]));

/** `current`'s values for exactly `keys`. */
export const pick = <T extends Record<string, unknown>>(
  current: T,
  keys: readonly (keyof T & string)[],
): Partial<T> => Object.fromEntries(keys.map((key) => [key, current[key]])) as Partial<T>;
